import {
  ContentConsentTypeEnum,
  ProgrammeTypeDto,
  ProvinceDto,
} from '@ecdlink/core';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  ButtonGroup,
  Typography,
  ButtonGroupTypes,
  renderIcon,
  BannerWrapper,
  Checkbox,
  ImageInput,
  Dialog,
  DialogPosition,
  classNames,
  FormInput,
  Dropdown,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import { useEffect, useState } from 'react';
import { useForm, useFormState, useWatch, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { staticDataSelectors } from '@store/static-data';
import * as styles from './programme-details.styles';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import Article from '@/components/article/article';
import {
  ProgrammeDetailsModel,
  ProgrammeDetailsSchema,
} from '@/schemas/trainee/programme-details';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import {
  SmartSpaceChecklisstStepsSteps,
  SmartSpaceChecklistProps,
  yesNoOptions,
} from '../../smart-space-checklist.types';
import { traineeSelectors } from '@/store/trainee';
import { AddressMap } from '../map/map';
import tool_R4c_form from '@/assets/tool_R4c_form.pdf';
import tool_R4b_form from '@/assets/tool_R4b_form.pdf';
import tool_R4a_form from '@/assets/tool_R4a_form.pdf';
import { coachSelectors } from '@/store/coach';
import { userSelectors } from '@/store/user';

export const ProgrammeDetails: React.FC<SmartSpaceChecklistProps> = ({
  setActiveStep,
  handleNextSection,
  visitId,
}) => {
  const visitSection = 'Programme details';

  const {
    getValues: getProgrammeFormValues,
    setValue: setProgrammeFormValue,
    register: programmeFormRegister,
    trigger: triggerR4bForm,
    control: programmeFormControl,
  } = useForm<ProgrammeDetailsModel>({
    resolver: yupResolver(ProgrammeDetailsSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const { isOnline } = useOnlineStatus();
  const { isValid } = useFormState({ control: programmeFormControl });
  const {
    haveTheTitleDeeds,
    ownTheProperty,
    unproclaimedLand,
    liveAtTheProperty,
    haveReadTheSmartStarterInformation,
    popiaConsent,
  } = useWatch<ProgrammeDetailsModel>({
    control: programmeFormControl,
    defaultValue: {},
  });
  const provinces = useSelector(staticDataSelectors.getProvinces);
  const [articleTitle, setArticleTitle] = useState<string>();
  const [presentArticle, setPresentArticle] = useState<boolean>(false);
  const [contentConsentTypeEnum, setContentConsentTypeEnum] =
    useState<ContentConsentTypeEnum>();
  const [r4bPhotoUrl, setR4bPhotoUrl] = useState<string>();
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const acceptedFormats = ['jpg, bmp'];
  const visitData = useSelector(traineeSelectors.getTraineeVisitData(visitId));
  const [showMap, setShowMap] = useState(false);
  const [displayPhotoDeleteWarning, setDisplayPhotoDeleteWarning] =
    useState<boolean>(false);
  const disableSection = true;
  const user = useSelector(userSelectors.getUser);
  const coach = useSelector(coachSelectors.getCoach);
  const isCoach = coach?.user?.id === user?.id;

  const [questions, setAnswers] = useState([
    {
      question:
        'I have read the SmartStarter Information Sheet on Child Protection and Confidentiality',
      answer: false,
    },
    {
      question:
        'By submitting your personal information on this site, you consent to the collection, processing and storage of your data in accordance with our Privacy Policy and Protection of Personal Information Act (POPIA).',
      answer: false,
    },
    {
      question: 'What is the name of your programme?',
      answer: '',
    },
    {
      question: ' What type of programme are you running or planning to run?',
      answer: '',
    },
    {
      question: 'Flat/ unit / apartment number',
      answer: '',
    },
    {
      question: 'Street address',
      answer: '',
    },
    {
      question: 'Suburb/area',
      answer: '',
    },
    {
      question: 'City',
      answer: '',
    },
    {
      question: 'Province',
      answer: '',
    },
    {
      question: 'Postal Code',
      answer: '',
    },
    {
      question:
        'Do you own the property where you will run your SmartStart programme?',
      answer: '',
    },
    {
      question: 'Do you have the Title Deeds for the property?',
      answer: '',
    },
    {
      question: 'Is the property on un-proclaimed land?',
      answer: '',
    },
    {
      question: 'Take a photo of a certified copy of the Title Deeds',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4c Affidavit - Property Ownership',
      answer: '',
    },
    {
      question: 'Do you live at the property?',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4a form - Agreement for premises to be uesd for early learning programme.',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4b form - Confirmation of Lease.',
      answer: '',
    },
  ]);

  // Sort questions alphabetically to prevent the random order of them.
  const sortedVisitData = (visitData ?? []).slice().sort((a, b) => {
    const questionA = (a.question ?? '').toLowerCase();
    const questionB = (b.question ?? '').toLowerCase();

    if (questionA < questionB) return -1;
    if (questionA > questionB) return 1;
    return 0;
  });

  const checkedquestion = (question: string) => {
    const isChecked = visitData?.find((item) => item?.question === question);
    return isChecked;
  };

  const programData = useSelector(staticDataSelectors.getProgrammeTypes);

  const displayArticle = async (
    key: ContentConsentTypeEnum | undefined,
    title: string
  ) => {
    if (!key) {
      setPresentArticle(true);
      setArticleTitle(title);
      return;
    }

    setContentConsentTypeEnum(key);
    setPresentArticle(true);
    setArticleTitle(title);
  };

  const setPhotoUrl = (imageUrl: string) => {
    setProgrammeFormValue('r4bPhoto', imageUrl);

    setR4bPhotoUrl(imageUrl);
    triggerR4bForm();
    setPhotoActionBarVisible(false);
  };

  const deletePhoto = () => {
    setProgrammeFormValue('r4bPhoto', '');
    setR4bPhotoUrl('');
    setDisplayPhotoDeleteWarning(false);
  };

  useEffect(() => {
    if (!visitData || !sortedVisitData || !Array.isArray(visitData)) return;

    const criteriaMap = [
      {
        field: 'haveReadTheSmartStarterInformation' as const,
        match:
          'smartstarter information sheet on child protection and confidentiality',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'popiaConsent' as const,
        match: 'By submitting',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'programmeName' as const,
        match: 'what is the name of',
        transform: (answer: any) => answer,
      },
      {
        field: 'municipality' as const,
        match: 'municipality',
        transform: (answer: any) => answer,
      },
      {
        field: 'programmeType' as const,
        match: 'type of programme',
        transform: (answer: any) => answer,
      },
      {
        field: 'area' as const,
        match: 'area',
        transform: (answer: any) => answer,
      },
      {
        field: 'ward' as const,
        match: 'ward',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine1' as const,
        match: 'address line 1',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine2' as const,
        match: 'address line 2',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine3' as const,
        match: 'city',
        transform: (answer: any) => answer,
      },
      {
        field: 'provinceId' as const,
        match: 'province',
        transform: (answer: any) => answer,
      },
      {
        field: 'postalCode' as const,
        match: 'postal code',
        transform: (answer: any) => answer,
      },
      {
        field: 'ownTheProperty' as const,
        match: 'own the property',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'haveTheTitleDeeds' as const,
        match: 'have the title deeds',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => answer,
      },
      {
        field: 'liveAtTheProperty' as const,
        match: 'live at the property?',
        transform: (answer: any) => answer,
      },
    ];

    criteriaMap.forEach(({ field, match, transform }) => {
      const found = sortedVisitData.find((x) =>
        x.question?.toLowerCase().includes(match.toLowerCase())
      );
      if (found?.questionAnswer != null) {
        setProgrammeFormValue(field, transform(found.questionAnswer));
      }
    });

    const r4bPhotoMatch = sortedVisitData.find(
      (x) =>
        x.question?.toLowerCase().includes('take a photo') &&
        x.questionAnswer != null &&
        x.questionAnswer !== ''
    );

    if (r4bPhotoMatch && r4bPhotoMatch.questionAnswer) {
      setProgrammeFormValue('r4bPhoto', r4bPhotoMatch.questionAnswer);
      setR4bPhotoUrl(r4bPhotoMatch.questionAnswer);
    }
  }, [visitData]);

  useEffect(() => {
    if (ownTheProperty === false) {
      setProgrammeFormValue('haveTheTitleDeeds', false);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  useEffect(() => {
    if (ownTheProperty === true) {
      setProgrammeFormValue('liveAtTheProperty', undefined);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  const onDownloadImageR4c = () => {
    const pdfUrl = tool_R4c_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4b = () => {
    const pdfUrl = tool_R4b_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4a = () => {
    const pdfUrl = tool_R4a_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4a_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  return (
    <>
      <BannerWrapper
        showBackground={false}
        size="medium"
        renderBorder={true}
        title={'Programme details'}
        subTitle={'Step 1 of 4'}
        color={'primary'}
        onBack={() => setActiveStep(SmartSpaceChecklisstStepsSteps.INITIAL)}
        displayOffline={!isOnline}
        renderOverflow={true}
      >
        <div
          className={`${
            disableSection ? 'pointer-events-none p-4 opacity-50' : ''
          }`}
        >
          <div>
            <Typography
              type={'h2'}
              text={'Programme details'}
              color={'textDark'}
              className={'my-3'}
            />
            {disableSection && (
              <Alert
                className="my-4"
                type="warning"
                title="You are viewing this form and cannot edit responses."
                list={
                  isCoach
                    ? ['This form should be filled in by the trainee.']
                    : []
                }
              />
            )}
            <Typography
              type={'h4'}
              text={'Child protection and confidentiality'}
              color={'textDark'}
              className={'my-3'}
            />
            <div className="flex items-center">
              <div className="'flex items-center' w-full flex-row justify-start">
                <div
                  className="flex items-start gap-2"
                  onClick={() => {
                    setProgrammeFormValue(
                      'haveReadTheSmartStarterInformation',
                      !getProgrammeFormValues()
                        .haveReadTheSmartStarterInformation
                    );
                  }}
                >
                  <Checkbox<ProgrammeDetailsModel>
                    checked={
                      Boolean(
                        checkedquestion(questions?.[0].question)?.questionAnswer
                      ) ||
                      getProgrammeFormValues()
                        .haveReadTheSmartStarterInformation
                    }
                    register={programmeFormRegister}
                    nameProp={'haveReadTheSmartStarterInformation'}
                    onCheckboxChange={() => {}}
                    value={
                      String(
                        getProgrammeFormValues()
                          .haveReadTheSmartStarterInformation
                      ) || checkedquestion(questions?.[0].question)?.question!
                    }
                    disabled={Boolean(checkedquestion(questions?.[0].question))}
                  ></Checkbox>
                  <Typography
                    text={questions?.[0].question}
                    type="body"
                    color={'textMid'}
                  />
                </div>
              </div>

              <Typography
                type={'body'}
                text={'View'}
                color={'secondary'}
                className={'my-3'}
                underline={true}
                onClick={() => {
                  displayArticle(
                    ContentConsentTypeEnum.ChildProtection,
                    'Child Protection'
                  );
                }}
              />
            </div>

            <Typography
              type={'h4'}
              text={'Protection of Personal Information Act, 4 of 2023 (POPIA)'}
              color={'textDark'}
              className={'my-3'}
            />

            <div className="flex items-center">
              <div className="'flex items-center' w-full flex-row justify-start">
                <div
                  className="flex items-start gap-2"
                  onClick={() => {
                    setProgrammeFormValue(
                      'popiaConsent',
                      !getProgrammeFormValues().popiaConsent
                    );
                  }}
                >
                  <Checkbox<ProgrammeDetailsModel>
                    checked={
                      Boolean(
                        checkedquestion(questions?.[1].question)?.questionAnswer
                      ) || getProgrammeFormValues().popiaConsent
                    }
                    register={programmeFormRegister}
                    nameProp={'popiaConsent'}
                    onCheckboxChange={() => {}}
                    value={
                      String(getProgrammeFormValues().popiaConsent) ||
                      checkedquestion(questions?.[1].question)?.question!
                    }
                    disabled={Boolean(checkedquestion(questions?.[1].question))}
                  ></Checkbox>
                  <Typography
                    text={questions?.[1].question}
                    type="body"
                    color={'textMid'}
                  />
                </div>
              </div>

              <Typography
                type={'body'}
                text={'View'}
                color={'secondary'}
                className={'my-3'}
                underline={true}
                onClick={() => {
                  displayArticle(ContentConsentTypeEnum.POPIA, 'POPIA Consent');
                }}
              />
            </div>
          </div>
          <div className="mt-3 space-y-4">
            <>
              <FormInput<ProgrammeDetailsModel>
                label={questions?.[2].question}
                register={programmeFormRegister}
                nameProp={'programmeName'}
                placeholder={'E.g. Little Lambs Preschool'}
                type={'text'}
                onChange={(e) => {}}
                disabled={Boolean(checkedquestion(questions?.[2].question))}
              ></FormInput>

              <div
                className={`w-full ${
                  Boolean(checkedquestion(questions?.[3].question))
                    ? 'pointer-events-none'
                    : ''
                }`}
              >
                <label className={styles.label}>
                  {questions?.[3].question}
                </label>
                <div className="mt-1">
                  <Controller
                    name={'programmeType'}
                    control={programmeFormControl}
                    render={({ field: { onChange, value, ref } }) => (
                      <ButtonGroup<string | string[]>
                        inputRef={ref}
                        options={
                          (programData &&
                            programData.map((x: ProgrammeTypeDto) => {
                              return { text: x.description, value: x.id ?? '' };
                            })) ||
                          []
                        }
                        multiple={false}
                        onOptionSelected={(e) => {
                          setProgrammeFormValue('programmeType', e as string);
                        }}
                        selectedOptions={value}
                        color="secondary"
                        type={ButtonGroupTypes.Button}
                        className={'w-full'}
                      />
                    )}
                  ></Controller>
                </div>
              </div>

              <FormInput<ProgrammeDetailsModel>
                label={questions?.[4].question}
                register={programmeFormRegister}
                nameProp={'addressLine1'}
                placeholder={'203 Oak Apartments'}
                type={'text'}
                onChange={(e) => {}}
                disabled={Boolean(checkedquestion(questions?.[4].question))}
              ></FormInput>

              <FormInput<ProgrammeDetailsModel>
                label={questions?.[5].question}
                register={programmeFormRegister}
                nameProp={'addressLine1'}
                placeholder={'E.g. 11 Green Road'}
                type={'text'}
                onChange={(e) => {}}
                disabled={Boolean(checkedquestion(questions?.[5].question))}
              ></FormInput>

              <FormInput<ProgrammeDetailsModel>
                label={questions?.[6].question}
                register={programmeFormRegister}
                nameProp={'addressLine2'}
                placeholder={'E.g. Mamelodi East'}
                type={'text'}
                onChange={(e) => {}}
                disabled={Boolean(checkedquestion(questions?.[6].question))}
              ></FormInput>

              <FormInput<ProgrammeDetailsModel>
                label={questions?.[7].question}
                register={programmeFormRegister}
                nameProp={'addressLine3'}
                placeholder={'E.g. Cape Town'}
                type={'text'}
                onChange={(e) => {}}
                disabled={Boolean(checkedquestion(questions?.[7].question))}
              ></FormInput>

              <Dropdown
                placeholder={'Choose province'}
                list={
                  (provinces &&
                    provinces.map((province: ProvinceDto) => ({
                      label: province.description,
                      value: province.id || '',
                    }))) ||
                  []
                }
                fillType="clear"
                fullWidth={true}
                label={'Province'}
                className={classNames(styles.divider, 'w-full')}
                selectedValue={getProgrammeFormValues().provinceId}
                onChange={(value: string) => {
                  setProgrammeFormValue('provinceId', value as string, {
                    shouldValidate: true,
                  });
                }}
              />

              <div className={'w-full'}>
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[8].question}
                  register={programmeFormRegister}
                  nameProp={'postalCode'}
                  placeholder={'E.g. 0081'}
                  type={'text'}
                  onChange={(e) => {}}
                  disabled={Boolean(checkedquestion(questions?.[9].question))}
                ></FormInput>
              </div>

              <div className={'w-full'}>
                <label className={styles.label}>
                  {questions?.[10].question}
                </label>
                <div
                  className={`mt-1 ${
                    Boolean(checkedquestion(questions?.[10].question))
                      ? 'pointer-events-none'
                      : ''
                  }`}
                >
                  <ButtonGroup<boolean | undefined>
                    options={yesNoOptions}
                    onOptionSelected={(value: any) => {
                      setProgrammeFormValue(
                        'ownTheProperty',
                        value as boolean,
                        {
                          shouldValidate: true,
                        }
                      );
                    }}
                    selectedOptions={[getProgrammeFormValues().ownTheProperty]}
                    color="secondary"
                    type={ButtonGroupTypes.Button}
                    className={'w-full'}
                  />
                </div>
              </div>

              {ownTheProperty === true && (
                <div className={'w-full'}>
                  <label className={styles.label}>
                    {questions?.[11].question}
                  </label>
                  <div
                    className={`mt-1 ${
                      Boolean(checkedquestion(questions?.[11].question))
                        ? 'pointer-events-none'
                        : ''
                    }`}
                  >
                    <ButtonGroup<boolean | undefined>
                      options={yesNoOptions}
                      onOptionSelected={(value: any) => {
                        setProgrammeFormValue(
                          'haveTheTitleDeeds',
                          value as boolean,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      selectedOptions={[
                        getProgrammeFormValues().haveTheTitleDeeds,
                      ]}
                      color="secondary"
                      type={ButtonGroupTypes.Button}
                      className={'w-full'}
                    />
                  </div>
                </div>
              )}
              {ownTheProperty === false && (
                <div className={'w-full'}>
                  <label className={styles.label}>
                    {questions?.[15].question}
                  </label>
                  <div
                    className={`mt-1 ${
                      Boolean(checkedquestion(questions?.[15].question))
                        ? 'pointer-events-none'
                        : ''
                    }`}
                  >
                    <ButtonGroup<boolean | undefined>
                      options={yesNoOptions}
                      onOptionSelected={(value: any) => {
                        setProgrammeFormValue(
                          'liveAtTheProperty',
                          value as boolean,
                          {
                            shouldValidate: true,
                          }
                        );
                      }}
                      selectedOptions={[
                        getProgrammeFormValues().liveAtTheProperty,
                      ]}
                      color="secondary"
                      type={ButtonGroupTypes.Button}
                      className={'w-full'}
                    />
                  </div>
                </div>
              )}
              {liveAtTheProperty === true && (
                <>
                  <Alert
                    className="mb-4"
                    type="info"
                    title={`The owner or landlord of the property will need to fill in the “confirmation of lease” with you (form R4b).`}
                    list={[
                      'Fill in the form with your landlord and upload a picture of it below.',
                    ]}
                    button={
                      <Button
                        onClick={onDownloadImageR4b}
                        text="Download the R4b form"
                        icon="DownloadIcon"
                        type={'filled'}
                        color={'primary'}
                        textColor={'white'}
                      />
                    }
                  />
                  <ImageInput<ProgrammeDetailsModel>
                    acceptedFormats={acceptedFormats}
                    resolutionLimit={IMAGE_WIDTH}
                    label={questions?.[16].question}
                    nameProp="r4bPhoto"
                    icon="CameraIcon"
                    className={'py-4'}
                    currentImageString={r4bPhotoUrl}
                    register={programmeFormRegister}
                    overrideOnClick={() => setPhotoActionBarVisible(true)}
                    onValueChange={(imageString: string) => {
                      setProgrammeFormValue('r4bPhoto', imageString);
                      triggerR4bForm();
                    }}
                    disabled={Boolean(
                      checkedquestion(questions?.[16].question)
                    )}
                  ></ImageInput>
                </>
              )}
              {liveAtTheProperty === false && (
                <>
                  <Alert
                    className="mb-4"
                    type="info"
                    title={`The owner or landlord of the property will need to fill in the “Agreement for premises to be used for early learning programme” with you (form 4a).`}
                    list={[
                      'Fill in the form with your landlord and upload a picture of it below.',
                    ]}
                    button={
                      <Button
                        onClick={onDownloadImageR4a}
                        text="Download the R4a form"
                        icon="DownloadIcon"
                        type={'filled'}
                        color={'primary'}
                        textColor={'white'}
                      />
                    }
                  />
                  <ImageInput<ProgrammeDetailsModel>
                    acceptedFormats={acceptedFormats}
                    resolutionLimit={IMAGE_WIDTH}
                    label={questions?.[15].question}
                    nameProp="r4bPhoto"
                    icon="CameraIcon"
                    className={'py-4'}
                    currentImageString={r4bPhotoUrl}
                    register={programmeFormRegister}
                    overrideOnClick={() => setPhotoActionBarVisible(true)}
                    onValueChange={(imageString: string) => {
                      setProgrammeFormValue('r4bPhoto', imageString);
                      triggerR4bForm();
                    }}
                    disabled={Boolean(
                      checkedquestion(questions?.[15].question)
                    )}
                  ></ImageInput>
                </>
              )}
            </>
            {haveTheTitleDeeds === false && ownTheProperty === true && (
              <div className={'w-full'}>
                <label className={styles.label}>
                  {questions?.[12].question}
                </label>
                <div
                  className={`mt-1 ${
                    Boolean(checkedquestion(questions?.[12].question))
                      ? 'pointer-events-none'
                      : ''
                  }`}
                >
                  <Controller
                    name="unproclaimedLand"
                    control={programmeFormControl}
                    render={({ field: { onChange, value, ref } }) => (
                      <ButtonGroup<boolean>
                        inputRef={ref}
                        options={yesNoOptions}
                        onOptionSelected={(value: any) => {
                          setProgrammeFormValue(
                            'unproclaimedLand',
                            value as boolean,
                            {
                              shouldValidate: true,
                            }
                          );
                        }}
                        selectedOptions={value}
                        color="secondary"
                        type={ButtonGroupTypes.Button}
                        className={'w-full'}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            {haveTheTitleDeeds === true && (
              <ImageInput<ProgrammeDetailsModel>
                acceptedFormats={acceptedFormats}
                resolutionLimit={IMAGE_WIDTH}
                label={questions?.[13].question}
                nameProp="r4bPhoto"
                icon="CameraIcon"
                className={'py-4'}
                currentImageString={r4bPhotoUrl}
                register={programmeFormRegister}
                overrideOnClick={() => setPhotoActionBarVisible(true)}
                onValueChange={(imageString: string) => {
                  setProgrammeFormValue('r4bPhoto', imageString);
                  triggerR4bForm();
                }}
                disabled={Boolean(checkedquestion(questions?.[13].question))}
              ></ImageInput>
            )}

            {unproclaimedLand === false && (
              <Alert
                className="mb-4"
                type="warning"
                title={`Apply to your municipality for a copy of the title deeds.`}
                list={[
                  'You will need to get a copy of the title deeds in order to complete this section.',
                  'Once you have the title deeds, answer “Yes” to the Title Deeds question above and add a photo of the document.',
                ]}
              />
            )}

            {unproclaimedLand === true && (
              <>
                <Alert
                  className="mb-4"
                  type="info"
                  title={`Please get a signed, stamped copy of an R4c form instead.`}
                  list={[
                    'This form can be stamped by any commissioner of oaths. This could include: police officers, South African Post Office workers, lawyers, or accountants for example.',
                  ]}
                  button={
                    <Button
                      onClick={onDownloadImageR4c}
                      text="Download the R4c form"
                      icon="DownloadIcon"
                      type={'filled'}
                      color={'primary'}
                      textColor={'white'}
                    />
                  }
                />
                <ImageInput<ProgrammeDetailsModel>
                  acceptedFormats={acceptedFormats}
                  resolutionLimit={IMAGE_WIDTH}
                  label={questions?.[14].question}
                  nameProp="r4bPhoto"
                  icon="CameraIcon"
                  className={'py-4'}
                  currentImageString={r4bPhotoUrl}
                  register={programmeFormRegister}
                  overrideOnClick={() => setPhotoActionBarVisible(true)}
                  onValueChange={(imageString: string) => {
                    setProgrammeFormValue('r4bPhoto', imageString);
                    triggerR4bForm();
                  }}
                  disabled={Boolean(checkedquestion(questions?.[14].question))}
                ></ImageInput>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="px-4">
            <Button
              type="filled"
              color="primary"
              className={styles.button}
              onClick={() => handleNextSection()}
            >
              {renderIcon('ArrowCircleRightIcon', 'mr-2 text-white w-5')}
              <Typography type={'help'} text={'Next'} color={'white'} />
            </Button>
          </div>
        </div>
      </BannerWrapper>

      {presentArticle && (
        <Article
          consentEnumType={contentConsentTypeEnum}
          visible={presentArticle}
          title={articleTitle}
          onClose={() => setPresentArticle(false)}
          isOpen={true}
        />
      )}

      {/* <Dialog visible={showMap} position={DialogPosition.Bottom} stretch>
        <AddressMap
          onClose={() => setShowMap?.(false)}
          onSubmit={(address) => {
            setProgrammeFormValue('addressLine1', address);
          }}
        />
      </Dialog> */}
      <Dialog
        visible={photoActionBarVisible}
        position={DialogPosition.Bottom}
        stretch
      >
        <div
          className={` ${
            Boolean(checkedquestion(questions?.[11].question))
              ? 'pointer-events-none'
              : ''
          }`}
        >
          <PhotoPrompt
            title={'R4b photo'}
            resolutionLimit={IMAGE_WIDTH}
            onClose={() => setPhotoActionBarVisible(false)}
            onAction={(imageUrl: string) => {
              setPhotoUrl(imageUrl);
            }}
            onDelete={() => deletePhoto()}
          ></PhotoPrompt>
        </div>
      </Dialog>
    </>
  );
};
